const DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const TEXT_PREVIEW_LIMIT = 1024 * 1024;

function classifyViewer(contentType, filename) {
  const type = (contentType || "").toLowerCase();
  const ext = (filename || "").toLowerCase().split(".").pop();

  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type.startsWith("image/")) return "image";
  if (type === DOCX_TYPE || ext === "docx") return "docx";
  if (type === XLSX_TYPE || ext === "xlsx") return "xlsx";
  if (type.startsWith("text/") || type === "application/json" || type === "application/xml") {
    return "text";
  }
  return "unsupported";
}

function humanFileSize(bytes) {
  if (!bytes || isNaN(bytes)) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function registerFilePreview(Alpine) {
  Alpine.data("filePreview", () => ({
    open: false,
    loading: false,
    filename: "",
    contentType: "",
    byteSize: 0,
    uploadedBy: "",
    previewUrl: "",
    htmlPreviewUrl: "",
    downloadUrl: "",
    viewer: "unsupported",
    textContent: "",
    previouslyFocused: null,

    get humanSize() {
      return humanFileSize(this.byteSize);
    },

    init() {
      this._openHandler = (event) => this.show(event.detail || {});
      window.addEventListener("file-preview:open", this._openHandler);
    },

    destroy() {
      window.removeEventListener("file-preview:open", this._openHandler);
    },

    show(detail) {
      this.previouslyFocused = document.activeElement;
      this.filename = detail.filename || "";
      this.contentType = detail.contentType || "";
      this.byteSize = Number(detail.byteSize || 0);
      this.uploadedBy = detail.uploadedBy || "";
      this.previewUrl = detail.previewUrl || "";
      this.htmlPreviewUrl = detail.htmlPreviewUrl || "";
      this.downloadUrl = detail.downloadUrl || "";
      this.textContent = "";
      this.viewer = classifyViewer(this.contentType, this.filename);
      this.open = true;
      document.body.style.overflow = "hidden";

      if (this.viewer === "docx" || this.viewer === "xlsx" || this.viewer === "text") {
        this.loading = true;
        this.$nextTick(() => this.loadAsync());
      } else {
        this.loading = false;
      }
    },

    async loadAsync() {
      try {
        if (this.viewer === "docx") await this.loadDocx();
        else if (this.viewer === "xlsx") await this.loadXlsx();
        else if (this.viewer === "text") await this.loadText();
      } catch (err) {
        console.error("file preview load failed", err);
        this.viewer = "unsupported";
      } finally {
        this.loading = false;
      }
    },

    async loadDocx() {
      const [{ renderAsync }, response] = await Promise.all([
        import("docx-preview"),
        fetch(this.previewUrl, { credentials: "same-origin" })
      ]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      this.loading = false;
      await this.$nextTick();
      const container = this.$refs.docxContainer;
      if (container) {
        container.innerHTML = "";
        await renderAsync(blob, container);
      }
    },

    async loadXlsx() {
      const url = this.htmlPreviewUrl || this.previewUrl;
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html" }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      this.loading = false;
      await this.$nextTick();
      const container = this.$refs.xlsxContainer;
      if (container) {
        container.innerHTML = html;
      }
    },

    async loadText() {
      const response = await fetch(this.previewUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      this.textContent = text.length > TEXT_PREVIEW_LIMIT ? text.slice(0, TEXT_PREVIEW_LIMIT) + "\n…" : text;
    },

    hide() {
      this.open = false;
      this.loading = false;
      this.textContent = "";
      document.body.style.overflow = "";
      if (this.$refs.docxContainer) this.$refs.docxContainer.innerHTML = "";
      if (this.$refs.xlsxContainer) this.$refs.xlsxContainer.innerHTML = "";

      if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
    },

    handleKeydown(event) {
      if (!this.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.hide();
      }
    }
  }));

  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[data-file-preview]");
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== undefined && event.button !== 0) return;

    event.preventDefault();
    window.dispatchEvent(
      new CustomEvent("file-preview:open", {
        detail: {
          filename: link.dataset.filename,
          contentType: link.dataset.contentType,
          byteSize: link.dataset.byteSize,
          uploadedBy: link.dataset.uploadedBy,
          previewUrl: link.dataset.previewUrl,
          htmlPreviewUrl: link.dataset.htmlPreviewUrl,
          downloadUrl: link.getAttribute("href")
        }
      })
    );
  });
}
